import { isEqual } from 'lodash';
import { FC, Fragment } from 'react';
import { FaEuroSign } from 'react-icons/fa6';
import { PiTrashDuotone } from 'react-icons/pi';
import { Button, Select } from 'rizzui';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import CustomSelect from '../../../components/forms/CustomSelect';
import CustomTextDelayInput from '../../../components/forms/CustomTextDelayInput';
import DateField from '../../../components/forms/DateField';
import useExpirationManagementStates from '../../../hooks/api-calls/expirations/useExpirationManagementStates';
import useExpirationStates from '../../../hooks/api-calls/expirations/useExpirationStates';
import usePaymentMethods from '../../../hooks/api-calls/expirations/usePaymentMethods';
import useUsers from '../../../hooks/api-calls/useUsers';
import { FilterOptions } from '../../../hooks/useFilters';


interface IExpirationFiltersProps {
    updateFilters: (filters: any) => void
    resetFilters: (limit: any) => void
    filters: FilterOptions
}

const ExpirationFilters: FC<IExpirationFiltersProps> = ({ filters, updateFilters, resetFilters }) => {

    // HOOKS

    const { userCan } = usePrivilege();
    const { getPaymentMethodsList } = usePaymentMethods();
    const { getUsersList } = useUsers();
    const { getExpirationStatesList } = useExpirationStates();
    const { getExpirationManagementStatesList } = useExpirationManagementStates();

    // RENDER

    return (
        <Fragment>
            <CustomSelect
                id="due_status"
                label="Estado de Vencimiento"
                isMulti={true}
                options={getExpirationStatesList()}
                value={filters.filter_filters?.due_status ?
                    getExpirationStatesList().filter((type) => filters.filter_filters?.due_status?.includes(type.value)) : []}
                onChange={(value: { value: string, label: string }[]) => {
                    const dueStatus = value.map((type) => type.value);
                    if (dueStatus !== filters.filter_filters?.due_status) {
                        updateFilters({ due_status: dueStatus });
                    }
                }}
            />

            <CustomSelect
                id="due_management_status"
                label="Estado de Gestión de Vencimiento"
                isMulti={true}
                options={getExpirationManagementStatesList()}
                value={filters.filter_filters?.due_management_status ?
                    getExpirationManagementStatesList().filter((type) => filters.filter_filters?.due_management_status?.includes(type.value)) : []}
                onChange={(value: { value: string, label: string }[]) => {
                    const dueStatus = value.map((type) => type.value);
                    if (dueStatus !== filters.filter_filters?.due_management_status) {
                        updateFilters({ due_management_status: dueStatus });
                    }
                }}
            />

            <CustomSelect
                id="payment_method"
                label="Método de Pago"
                isMulti={true}
                options={getPaymentMethodsList()}
                value={filters.filter_filters?.payment_method ?
                    getPaymentMethodsList().filter((type) => filters.filter_filters?.payment_method?.includes(type.value)) : []}
                onChange={(value: { value: string, label: string }[]) => {
                    const paymentMethods = value.map((type) => type.value);
                    if (paymentMethods !== filters.filter_filters?.payment_method) {
                        updateFilters({ payment_method: paymentMethods });
                    }
                }}
            />

            <CustomSelect
                isSearchable
                id={'show_deleted'}
                label="Mostrar Eliminados"
                isClearable
                options={[
                    { label: 'Todos', value: null },
                    { label: 'Si', value: true },
                ]}
                onChange={(e: any) => {
                    if (e.value === null) {
                        updateFilters({ show_deleted: null });
                        return;
                    }
                    updateFilters({ show_deleted: e.value });
                }}
                defaultValue={null}
                value={filters.filter_filters?.show_deleted !== null && filters.filter_filters?.show_deleted !== undefined
                    ? { label: filters.filter_filters?.show_deleted ? 'Si' : 'Todos', value: filters.filter_filters?.show_deleted }
                    : { label: 'Todos', value: null }}
            />

            {userCan('get_users_for_select', 'user') && (
                <CustomSelect
                    id="reminder_responsible"
                    label="Responsable del Recordatorio"
                    isMulti={true}
                    options={getUsersList()}
                    value={filters.filter_filters?.reminder_responsible ?
                        getUsersList().filter((type) => filters.filter_filters?.reminder_responsible?.includes(type.value)) : []}
                    onChange={(value: { value: string, label: string }[]) => {
                        const reminderResponsible = value.map((type) => type.value);
                        if (reminderResponsible !== filters.filter_filters?.reminder_responsible) {
                            updateFilters({ reminder_responsible: reminderResponsible });
                        }
                    }}
                />
            )}

            <DateField
                isClearable
                className="w-full"
                label="Fecha de Vencimiento"
                placeholderText="Selecciona las fechas"
                selectedDateField={"expiration_date"}
                startDate={filters.filter_filters?.expiration_date?.startDate ? new Date(filters.filter_filters.expiration_date.startDate) : null}
                endDate={filters.filter_filters?.expiration_date?.endDate ? new Date(filters.filter_filters.expiration_date.endDate) : null}
                onChange={(date: any) => {
                    const filters_expiration_date = filters.filter_filters?.expiration_date;
                    const expiration_date = date ? { startDate: date[0], endDate: date[1] } : null;
                    if (expiration_date && !isEqual(filters_expiration_date, expiration_date)) {
                        updateFilters({ expiration_date: { startDate: date[0], endDate: date[1] } });
                    } else if (!expiration_date && filters_expiration_date) {
                        updateFilters({ expiration_date: null });
                    }
                }}
            />

            <CustomTextDelayInput
                id="notes"
                label="Notas"
                filters={filters}
                updateFilters={updateFilters}
            />

            <div className='md:grid md:grid-cols-2 md:gap-4'>
                <CustomTextDelayInput
                    id="min_amount"
                    label="Importe Mínimo"
                    filters={filters}
                    updateFilters={updateFilters}
                    type='number'
                    suffix={<FaEuroSign />}
                />
                <CustomTextDelayInput
                    id="max_amount"
                    label="Importe Máximo"
                    filters={filters}
                    updateFilters={updateFilters}
                    type='number'
                    suffix={<FaEuroSign />}
                />
            </div>

            <Button
                size="sm"
                onClick={() => {
                    resetFilters(50);
                }}
                variant="flat"
                className="h-9 bg-gray-200/70"
            >
                <PiTrashDuotone className="me-1.5 h-[17px] w-[17px]" /> Limpiar
            </Button>
        </Fragment>
    )
}

export default ExpirationFilters;